import {
    faCode,
    faCrosshairs,
    faPlus,
    faServer,
    faSliders,
    faTrophy,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { lazy, Suspense, useEffect, useState } from 'react'
import { IsDeveloper, useAuth } from '~/components/Auth'
import { Allowed, ROLES } from '../components/Capabilities'
import { Box } from '~/shared/ui'
import { LaunchBoard } from './servers/LaunchBoard'
import { ServersBoard } from './servers/ServersBoard'
import { RemoteConfig } from '../remote_config'
import { Weapons } from '../weapons'
import { WorldClocks } from '../components/WorldClocks'

// Lazy, as they were when each had its own route: Tournaments and the API
// inspector are large screens and most visits here never open them.
const Tournaments = lazy(() => import('../tournaments'))
const ApiInspector = lazy(() =>
    import('../api_inspector').then((module) => ({ default: module['ApiInspector'] }))
)

// Server Overview first, and the page opens on it: what the fleet is doing
// right now is what most visits are for (the owner's call, 2026-10-12). Then
// the order the work happens: decide whether the game is open, build the
// servers, put a tournament on them, then dig into what they answered.
//
// Remote config sits here rather than in its own top-level entry: it is the
// same job -- deciding what the fleet is doing -- and one of its keys decides
// whether players can get in, which is the first thing anybody on this page
// wants to know.
//
// Tournaments and the API inspector were top-level entries too. A tournament
// is built out of the servers made on the tab beside it, so reaching it meant
// leaving the page that makes them.
//
// Cheat reports and Player integrity were tabs here. They moved to Auto Mod
// under Accounts, which is where the accounts they are about are worked on:
// reading a finding here meant leaving for that screen to act on it and
// coming back. The panels themselves are unchanged and still live in
// `./servers/`, because they read this section's own api module.
//
// Players online folded into Server Overview: the same fleet read from two
// angles with a click between them. The roster is behind each row's count.
// An icon each, in the order the work happens. They are shorthand, not
// decoration: six tabs of similar-length words read as one grey strip, and the
// icon is what the eye returns to after a week away.
const TABS = [
    { key: 'servers', label: 'Server Overview', icon: faServer },
    { key: 'config', label: 'Game Set Up', icon: faSliders },
    // Discord Control is NOT here. What a kill race says when it posts is a
    // community job, not a fleet one, and it lives under Community beside the
    // other things players see and send.
    { key: 'templates', label: 'Server Creation', icon: faPlus },
    { key: 'tournaments', label: 'Tournaments', icon: faTrophy },
    { key: 'weapons', label: 'Weapon Balance', icon: faCrosshairs },
    { key: 'api', label: 'API Inspector', icon: faCode },
] as const

export const ServerControl = () => {
    const { auth } = useAuth()
    const [tab, setTab] = useState<(typeof TABS)[number]['key']>('servers')
    // "Running on N" on the Server Creation tab lands here, filtered to that kind.
    const [profile, setProfile] = useState<{ id: number; name: string } | null>(null)

    // Authoring a template decides what a server costs and how much it pays
    // out, so the backend restricts writes to executives. Reflected here by
    // disabling rather than hiding: a developer can still see what exists and
    // read why they cannot change it.
    const canEditTemplates = auth?.is_executive === true
    // Building servers and operating them (restart, stop, destroy, drain,
    // approvals) is DeveloperPermission on the backend, which executives also
    // hold. The new board gated building on executive, so developers who are
    // allowed to deploy found the button dead; marketing staff, who can open
    // this page but hold neither, got live buttons that answered 403.
    const canOperate = auth?.is_developer === true || auth?.is_executive === true
    // Tournaments is marketing's, or a granted capability; the inspector is
    // developer-only. Each tab is drawn only for the people its own endpoints
    // would answer, and the gate inside still decides.
    const canRunTournaments =
        auth?.is_marketing === true || auth?.is_executive === true || auth?.is_developer === true

    const visible = TABS.filter((t) => {
        if (t.key === 'config' || t.key === 'api' || t.key === 'weapons') return canOperate
        if (t.key === 'tournaments') return canRunTournaments
        return true
    })

    // The page opens on the first tab, but the first tab is gated and `auth`
    // arrives after the first render, so a marketing account would land on a
    // tab it cannot see and get an empty page. Fall through to the first one
    // it can.
    useEffect(() => {
        if (visible.length && !visible.some((t) => t.key === tab)) setTab(visible[0].key)
    }, [visible.map((t) => t.key).join(), tab])

    return (
        <Allowed roles={ROLES.marketingOrDeveloper} capability='view_servers'>
            <Box>
                <header className='uppercase font-bold text-center py-2 text-2xl'>Game Control</header>
                <Box className='border border-white/10 rounded-lg overflow-hidden py-8'>
                    <div className='bg-custom-gradient-1 h-1 absolute top-0 left-0 w-full'></div>
                    {/* Clocks down the left, the work on the right.
                      *
                      * Every time on this page is shown in the one zone picked
                      * at the top of Team Tools, which is right for entering a
                      * time and wrong for deciding one: "build it at 18:00
                      * Eastern" is a question about whether anybody in Europe
                      * or South East Asia is awake. That arithmetic was being
                      * done in people's heads.
                      *
                      * Stacked beside the content on a wide screen and folded
                      * above it on a narrow one, because four clocks in a
                      * column on a phone is most of the screen. */}
                    <div className='flex flex-col gap-4 p-4 pl-12 lg:flex-row'>
                        <aside className='shrink-0 lg:w-36'>
                            <WorldClocks />
                        </aside>

                        <div className='flex min-w-0 flex-1 flex-col gap-6'>
                        <nav className='flex flex-row gap-2 border-b border-white/10'>
                            {visible.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setTab(t.key)}
                                    className={[
                                        'px-4 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px',
                                        tab === t.key
                                            ? 'border-amber-500 text-white'
                                            : 'border-transparent text-white/50 hover:text-white/80',
                                    ].join(' ')}
                                >
                                    <FontAwesomeIcon icon={t.icon} className='pr-2 opacity-60' />
                                    {t.label}
                                </button>
                            ))}
                        </nav>

                        {/* The legend went: every row carries an "i" that explains
                            its own status, with this server's reasons attached. */}
                        {tab === 'servers' && (
                            <ServersBoard
                                profile={profile}
                                onClearProfile={() => setProfile(null)}
                                canEditSchedules={canEditTemplates}
                                canOperate={canOperate}
                            />
                        )}
                        {/* Developer-gated on the server too, so this is a
                            courtesy rather than the control. */}
                        {tab === 'config' && canOperate && <RemoteConfig />}
                        {tab === 'weapons' && canOperate && <Weapons />}
                        {tab === 'tournaments' && canRunTournaments && (
                            <Suspense fallback={<span className='text-xs text-white/40'>loading…</span>}>
                                <Allowed roles={ROLES.marketing} capability='manage_tournaments' show403={true}>
                                    <Tournaments />
                                </Allowed>
                            </Suspense>
                        )}
                        {tab === 'api' && canOperate && (
                            <Suspense fallback={<span className='text-xs text-white/40'>loading…</span>}>
                                <IsDeveloper show403={true}>
                                    <ApiInspector />
                                </IsDeveloper>
                            </Suspense>
                        )}
                        {tab === 'templates' && (
                            <LaunchBoard
                                canEdit={canEditTemplates}
                                canDeploy={canOperate}
                                onShowServers={(t) => {
                                    setProfile({ id: t.id, name: t.name })
                                    setTab('servers')
                                }}
                            />
                        )}
                        </div>
                    </div>
                </Box>
            </Box>
        </Allowed>
    )
}
